/**
 * PlayerFighters — escort fighters the player buys (Mk IV Fighter Bays) and
 * assigns to fleet slots. While combat is active they launch from the hull, seek
 * the nearest hostile (raider fighter or cruiser) and dogfight it — same base
 * size / speed / fire rate as an enemy fighter. A downed fighter re-launches
 * after a delay; when combat ends the survivors fly home and dock.
 *
 * Each fighter's stats are (base × its pilot buff): pilot buffs raise speed, HP,
 * shield and fire rate. With no pilot the muls are all 1 (enemy-fighter parity).
 */
import { Vector2 } from "@/math/Vector2";
import { PLAYER_FIGHTER, FIGHTER_MISSILE } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";
import type { Damageable } from "@/game/combat/turrets";
import type { EnemyBullet, EnemyShip } from "./EnemyShips";

/** Per-slot fighter configuration (derived from the assigned pilot's buff). */
export interface FighterLoadout {
  pilotId: string | null; // the flying pilot — so a downed fighter can retire its pilot (KIA)
  speedMul: number;
  hpMul: number;
  shieldMul: number;
  fireMul: number; // >1 = faster firing
}

export interface FighterTurret {
  angle: number;
  cooldown: number;
  flash: number;
}

type FighterMode = "run" | "straight";

export interface PlayerFighter {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  turrets: FighterTurret[]; // length 2
  mode: FighterMode;
  straightTimer: number;
  launchTimer: number; // >0 → flying straight out of the hangar (no targeting yet)
  pilotId: string | null; // whose fighter this is (for KIA on death)
  // resolved stats (base × pilot buff)
  spd: number;
  fireInterval: number;
  // Mk VI Swarm-lord per-unit abilities (0 unless the unlock nodes are owned).
  missileCd: number; // seconds until this fighter's/drone's next homing-missile volley
}

export interface PlayerFighterCtx {
  originX: number; // hull position — launch + rally point
  originY: number;
  heading: number; // mothership heading — the ^ formation forms up behind it
  gates: { x: number; y: number }[]; // side hangar mouths — fighters launch from these
  launchForward?: boolean; // launch flying FORWARD (nose dir) instead of radially out (wing runways)
  launchBatch?: number; // craft to launch per wave (2 = a side-by-side pair per wing runway)
  ringFormation?: boolean; // recall = 2 spinning defensive rings (Swarm-lord) vs the ^ chevron
  damageMul?: number; // uniform bolt-damage buff for this whole swarm (FDC → drones)
  active: boolean; // combat active → deploy & fight
  hostiles: Damageable[]; // raiders + cruisers to attack
  raiders: EnemyShip[]; // enemy fighters — lets a fighter turn on whoever is hunting it
  incoming: EnemyBullet[]; // hostile bolts that can hit fighters
  bodies: MineableBody[]; // rocks to steer around
  onHit: (x: number, y: number) => void;
  onFighterDown: (x: number, y: number) => void;
  onPilotKilled: (pilotId: string | null) => void; // fighter destroyed → its pilot is KIA
  canMissile?: boolean; // Mk VI SWL: escort fighters / drones auto-fire homing missiles
  missileCount?: number; // missiles per volley (fighters default 2; drones 1)
}

/** A fighter-launched homing missile (Mk VI SWL ability); homes on the nearest live hostile. */
export interface FighterMissile {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  straight: number; // seconds flying straight before homing
  life: number;
}

const LAUNCH_STRAIGHT = 1; // fly straight out of the hangar for 1s before targeting
const POST_COMBAT_ROAM = 5; // roam around the ship 5s after combat before docking
const LAUNCH_INTERVAL = 0.55; // stagger between one-by-one hangar launches
const FORMATION_HOLD = 10; // seconds the ^ formation holds once everyone is in place
const FORMATION_BACK = 78; // per-rank distance behind the mothership
const FORMATION_SIDE = 62; // per-rank lateral spread (makes the ^ arms)
// Swarm-lord defensive rings: 2 concentric rings that spin opposite ways.
const RING_INNER = 130;
const RING_OUTER = 210;
const RING_SPIN = 0.6; // rad/s — the rings rotate around the mothership

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/** Per-craft base stats — escort fighters use PLAYER_FIGHTER; fighter drones pass
 * a tuned set (faster, weaker, lower damage). Only these four differ. */
export interface FighterTuning {
  speed: number;
  maxHp: number;
  maxShield: number;
  boltDamage: number;
}

export class PlayerFighterManager {
  readonly ships: PlayerFighter[] = [];
  readonly bullets: EnemyBullet[] = [];
  readonly missiles: FighterMissile[] = []; // Mk VI SWL: fighter + drone homing missiles (persist + home)
  private spawnTimer = 0; // gates the one-by-one launches
  private launchToggle = 0; // alternates the hangar gate
  private idleTime = 0; // seconds since combat ended (post-combat roam)
  private formationMode = false; // manual "call fighters" parade
  private formationHold = -1; // >=0 = counting the 10s hold (once all are in slots)
  private ringSpin = 0; // accumulating angle for the Swarm-lord defensive rings
  // Base stats (escort-fighter defaults; drones override via the constructor).
  private readonly spd0: number;
  private readonly hp0: number;
  private readonly sh0: number;
  private readonly dmg0: number;

  constructor(tune?: FighterTuning) {
    this.spd0 = tune?.speed ?? PLAYER_FIGHTER.speed;
    this.hp0 = tune?.maxHp ?? PLAYER_FIGHTER.maxHp;
    this.sh0 = tune?.maxShield ?? PLAYER_FIGHTER.maxShield;
    this.dmg0 = tune?.boltDamage ?? PLAYER_FIGHTER.boltDamage;
  }

  /** Toggle the ^ formation: call it to muster, click again to dismiss (dock). */
  callFormation(): void {
    if (this.formationMode) {
      this.formationMode = false;
      this.formationHold = -1;
      this.idleTime = POST_COMBAT_ROAM; // fall out → fly straight home to dock
    } else {
      this.formationMode = true;
      this.formationHold = -1;
    }
  }
  get formationActive(): boolean {
    return this.formationMode;
  }

  reset(): void {
    this.ships.length = 0;
    this.bullets.length = 0;
    this.missiles.length = 0;
    this.spawnTimer = 0;
    this.idleTime = 0;
    this.launchToggle = 0;
    this.formationMode = false;
    this.formationHold = -1;
  }

  private nextGate(ctx: PlayerFighterCtx): { x: number; y: number } | undefined {
    if (ctx.gates.length === 0) return undefined;
    const g = ctx.gates[this.launchToggle % ctx.gates.length]!;
    this.launchToggle++;
    return g;
  }

  private make(
    load: FighterLoadout,
    ctx: PlayerFighterCtx,
    gate?: { x: number; y: number },
  ): PlayerFighter {
    const maxHp = this.hp0 * load.hpMul;
    const maxShield = this.sh0 * load.shieldMul;
    // Launch from a hangar gate. Wing runways (launchForward) send them straight
    // out the NOSE; side gates send them radially away from the hull.
    let px: number;
    let py: number;
    let heading: number;
    if (gate) {
      px = gate.x;
      py = gate.y;
      heading = ctx.launchForward
        ? ctx.heading
        : Math.atan2(gate.y - ctx.originY, gate.x - ctx.originX);
    } else {
      const a = Math.random() * Math.PI * 2;
      px = ctx.originX + Math.cos(a) * 40;
      py = ctx.originY + Math.sin(a) * 40;
      heading = a;
    }
    return {
      pos: new Vector2(px, py),
      vel: new Vector2(),
      heading,
      hp: maxHp,
      maxHp,
      shield: maxShield,
      maxShield,
      shieldFlash: 0,
      hitTimer: 999,
      turrets: [
        { angle: heading, cooldown: Math.random() * PLAYER_FIGHTER.fireInterval, flash: 0 },
        { angle: heading, cooldown: Math.random() * PLAYER_FIGHTER.fireInterval, flash: 0 },
      ],
      mode: "run",
      straightTimer: 0,
      launchTimer: LAUNCH_STRAIGHT,
      pilotId: load.pilotId,
      spd: this.spd0 * load.speedMul,
      fireInterval: PLAYER_FIGHTER.fireInterval / Math.max(0.2, load.fireMul),
      missileCd: FIGHTER_MISSILE.cooldown * Math.random(), // stagger the first volley
    };
  }

  private applyLoadout(s: PlayerFighter, load: FighterLoadout): void {
    s.pilotId = load.pilotId;
    s.maxHp = this.hp0 * load.hpMul;
    s.maxShield = this.sh0 * load.shieldMul;
    s.spd = this.spd0 * load.speedMul;
    s.fireInterval = PLAYER_FIGHTER.fireInterval / Math.max(0.2, load.fireMul);
    if (s.hp > s.maxHp) s.hp = s.maxHp;
    if (s.shield > s.maxShield) s.shield = s.maxShield;
  }

  update(dt: number, loadouts: FighterLoadout[], ctx: PlayerFighterCtx): void {
    // Ring craft (Swarm-lord drones) hold the spinning defensive ring whenever
    // mustered — even in combat. Others: ^ parade out of combat, guard in combat.
    const holdFormation =
      this.formationMode && (!!ctx.ringFormation || !ctx.active);
    const guard = this.formationMode && ctx.active;
    // Fighters are fielded while combat is active OR while a formation is mustered.
    // When both end they fly home and dock (removed on arrival in updateShip).
    if (ctx.active || this.formationMode) {
      this.idleTime = 0;
      if (this.ships.length > loadouts.length) this.ships.length = loadouts.length;
      // Launch in waves: normally 1, but wing runways send out a side-by-side pair
      // (batch) per wave — alternating wings via nextGate.
      this.spawnTimer -= dt;
      if (this.ships.length < loadouts.length && this.spawnTimer <= 0) {
        const batch = Math.max(1, Math.floor(ctx.launchBatch ?? 1));
        for (let k = 0; k < batch && this.ships.length < loadouts.length; k++) {
          const gate = this.nextGate(ctx);
          this.ships.push(this.make(loadouts[this.ships.length]!, ctx, gate));
        }
        this.spawnTimer = LAUNCH_INTERVAL;
      }
      for (let i = 0; i < this.ships.length; i++) {
        this.applyLoadout(this.ships[i]!, loadouts[i]!);
      }
    } else {
      this.idleTime += dt;
    }
    if (holdFormation) {
      this.updateFormation(dt, ctx, loadouts.length);
    } else {
      for (let i = this.ships.length - 1; i >= 0; i--) {
        if (!this.updateShip(this.ships[i]!, dt, ctx, guard)) this.ships.splice(i, 1);
      }
    }

    // Friendly bolts → damage the nearest hostile they strike.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      for (const h of ctx.hostiles) {
        if (h.hp <= 0) continue;
        const rr = 30;
        if ((h.pos.x - b.x) ** 2 + (h.pos.y - b.y) ** 2 < rr * rr) {
          this.damage(h, this.dmg0 * (ctx.damageMul ?? 1));
          ctx.onHit(b.x, b.y);
          hit = true;
          break;
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }

    // Mk VI SWL fighter homing missiles — fly straight briefly, then home on the nearest
    // live hostile each frame (so they re-acquire automatically if their prey dies), then
    // detonate on contact / at end of life with a small splash.
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const m = this.missiles[i]!;
      m.life -= dt;
      if (m.straight > 0) {
        m.straight -= dt;
      } else {
        let tx = 0;
        let ty = 0;
        let best = Infinity;
        for (const h of ctx.hostiles) {
          if (h.hp <= 0) continue;
          const d = (h.pos.x - m.pos.x) ** 2 + (h.pos.y - m.pos.y) ** 2;
          if (d < best) {
            best = d;
            tx = h.pos.x;
            ty = h.pos.y;
          }
        }
        if (best < Infinity) {
          const turn = FIGHTER_MISSILE.turnRate * dt;
          const diff = norm(Math.atan2(ty - m.pos.y, tx - m.pos.x) - m.heading);
          m.heading += diff > turn ? turn : diff < -turn ? -turn : diff;
        }
      }
      m.vel.set(Math.cos(m.heading) * FIGHTER_MISSILE.speed, Math.sin(m.heading) * FIGHTER_MISSILE.speed);
      m.pos.x += m.vel.x * dt;
      m.pos.y += m.vel.y * dt;
      let boom = m.life <= 0;
      if (!boom) {
        const hr = FIGHTER_MISSILE.hitRadius;
        for (const h of ctx.hostiles) {
          if (h.hp <= 0) continue;
          if ((h.pos.x - m.pos.x) ** 2 + (h.pos.y - m.pos.y) ** 2 < hr * hr) {
            boom = true;
            break;
          }
        }
      }
      if (boom) {
        const br = FIGHTER_MISSILE.blastRadius;
        for (const h of ctx.hostiles) {
          if (h.hp <= 0) continue;
          if ((h.pos.x - m.pos.x) ** 2 + (h.pos.y - m.pos.y) ** 2 < br * br) {
            this.damage(h, FIGHTER_MISSILE.damage * (ctx.damageMul ?? 1));
          }
        }
        ctx.onHit(m.pos.x, m.pos.y);
        this.missiles.splice(i, 1);
      }
    }

    // Incoming hostile bolts → damage fighters (consume the bolt on a hit).
    for (const b of ctx.incoming) {
      if (b.life <= 0) continue;
      for (const s of this.ships) {
        if (s.hp <= 0) continue;
        const rr = PLAYER_FIGHTER.collisionRadius + 6;
        if ((s.pos.x - b.x) ** 2 + (s.pos.y - b.y) ** 2 < rr * rr) {
          this.damage(s, 8);
          b.life = -1; // consumed — the owning manager drops it next frame
          break;
        }
      }
    }
  }

  /** Fly each fighter to its slot in a ^ formation behind the mothership; hold the
   * shape for FORMATION_HOLD seconds once the last one is in place, then fall out. */
  private updateFormation(dt: number, ctx: PlayerFighterCtx, count: number): void {
    if (ctx.ringFormation) {
      this.updateRingFormation(dt, ctx);
      return;
    }
    const fx = Math.cos(ctx.heading);
    const fy = Math.sin(ctx.heading);
    const bx = -fx; // backward — the formation trails behind the mothership
    const by = -fy;
    const px = -fy; // perpendicular — spreads the ^ arms
    const py = fx;
    let allIn = count > 0 && this.ships.length >= count;
    for (let i = 0; i < this.ships.length; i++) {
      const s = this.ships[i]!;
      s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
      for (const t of s.turrets) {
        t.flash = Math.max(0, t.flash - dt * 5);
        t.cooldown -= dt;
      }
      // Just launched → fly straight out of the hangar for a beat before forming up.
      if (s.launchTimer > 0) {
        s.launchTimer -= dt;
        this.steer(s, dt, s.heading, s.spd);
        for (const t of s.turrets) t.angle = s.heading;
        allIn = false;
        continue;
      }
      const side = i % 2 === 0 ? -1 : 1;
      const rank = Math.floor(i / 2) + 1;
      const sx =
        ctx.originX + bx * rank * FORMATION_BACK + px * side * rank * FORMATION_SIDE;
      const sy =
        ctx.originY + by * rank * FORMATION_BACK + py * side * rank * FORMATION_SIDE;
      const ddx = sx - s.pos.x;
      const ddy = sy - s.pos.y;
      const d = Math.hypot(ddx, ddy);
      if (d > 10) {
        allIn = false;
        let desired = Math.atan2(ddy, ddx);
        desired = this.avoidRocks(s, ctx.bodies, desired);
        this.steer(s, dt, desired, Math.min(s.spd, 30 + d * 3));
      } else {
        // Parked in slot → face the way the mothership faces; ease onto the spot.
        const diff = norm(ctx.heading - s.heading);
        const maxTurn = PLAYER_FIGHTER.turnRate * dt;
        s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
        s.pos.x += ddx * Math.min(1, dt * 4);
        s.pos.y += ddy * Math.min(1, dt * 4);
        s.vel.set(0, 0);
      }
      for (const t of s.turrets) t.angle = s.heading;
    }
    if (allIn) {
      if (this.formationHold < 0) this.formationHold = FORMATION_HOLD;
      this.formationHold -= dt;
      if (this.formationHold <= 0) {
        // Hold's up → fall out and fly straight home to dock.
        this.formationMode = false;
        this.formationHold = -1;
        this.idleTime = POST_COMBAT_ROAM; // skip the roam → head straight for the hangar
      }
    } else {
      this.formationHold = -1;
    }
  }

  /** Swarm-lord recall: form TWO concentric rings that spin in opposite directions
   * around the mothership, each craft facing outward and gunning down any hostile
   * that drifts into its line of sight. Holds (spinning) until dismissed. */
  private updateRingFormation(dt: number, ctx: PlayerFighterCtx): void {
    this.ringSpin += dt * RING_SPIN;
    // Clear out any downed craft first (pilotless drones just re-launch from the wings).
    for (let i = this.ships.length - 1; i >= 0; i--) {
      const s = this.ships[i]!;
      if (s.hp <= 0) {
        ctx.onFighterDown(s.pos.x, s.pos.y);
        ctx.onPilotKilled(s.pilotId);
        this.ships.splice(i, 1);
      }
    }
    const total = this.ships.length;
    const innerN = Math.ceil(total / 2);
    for (let i = 0; i < total; i++) {
      const s = this.ships[i]!;
      s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
      s.hitTimer += dt;
      for (const t of s.turrets) {
        t.flash = Math.max(0, t.flash - dt * 5);
        t.cooldown -= dt;
      }
      this.updateAbilities(s, dt, ctx); // Mk VI SWL: drone beams / fighter missiles
      // Just launched → fly straight out for a beat before joining the ring.
      if (s.launchTimer > 0) {
        s.launchTimer -= dt;
        this.steer(s, dt, s.heading, s.spd);
        for (const t of s.turrets) t.angle = s.heading;
        continue;
      }
      const inner = i < innerN;
      const ringN = inner ? innerN : total - innerN;
      const local = inner ? i : i - innerN;
      const radius = inner ? RING_INNER : RING_OUTER;
      const dir = inner ? 1 : -1; // rings counter-rotate
      const ang = this.ringSpin * dir + (local / Math.max(1, ringN)) * Math.PI * 2;
      const sx = ctx.originX + Math.cos(ang) * radius;
      const sy = ctx.originY + Math.sin(ang) * radius;
      const ddx = sx - s.pos.x;
      const ddy = sy - s.pos.y;
      const d = Math.hypot(ddx, ddy);
      if (d > 24) {
        const desired = this.avoidRocks(s, ctx.bodies, Math.atan2(ddy, ddx));
        this.steer(s, dt, desired, Math.min(s.spd, 40 + d * 3));
      } else {
        // Parked on the ring → face radially OUTWARD and ease onto the moving slot.
        const diff = norm(ang - s.heading);
        const maxTurn = PLAYER_FIGHTER.turnRate * dt;
        s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
        s.pos.x += ddx * Math.min(1, dt * 5);
        s.pos.y += ddy * Math.min(1, dt * 5);
        s.vel.set(0, 0);
      }
      for (const t of s.turrets) t.angle = s.heading;
      // Fire outward at any hostile crossing this craft's line of sight.
      if (this.hostileInArc(s, ctx)) this.fireForward(s);
      if (s.hitTimer >= PLAYER_FIGHTER.regenDelay && s.shield < s.maxShield) {
        s.shield = Math.min(s.maxShield, s.shield + PLAYER_FIGHTER.shieldRegenPerSec * dt);
      }
    }
    this.formationHold = -1; // rings spin indefinitely while mustered
  }

  /** True if a live hostile sits within this craft's forward firing cone + range. */
  private hostileInArc(s: PlayerFighter, ctx: PlayerFighterCtx): boolean {
    const rangeSq = PLAYER_FIGHTER.turretRange * PLAYER_FIGHTER.turretRange;
    for (const h of ctx.hostiles) {
      if (h.hp <= 0) continue;
      const dx = h.pos.x - s.pos.x;
      const dy = h.pos.y - s.pos.y;
      if (dx * dx + dy * dy > rangeSq) continue;
      if (Math.abs(norm(Math.atan2(dy, dx) - s.heading)) < PLAYER_FIGHTER.fireCone) return true;
    }
    return false;
  }

  /** Returns false when the fighter should be removed from the fleet this frame. */
  private updateShip(
    s: PlayerFighter,
    dt: number,
    ctx: PlayerFighterCtx,
    guard: boolean,
  ): boolean {
    if (s.hp <= 0) {
      // A downed fighter is lost for good and its pilot is Killed In Action —
      // no respawn. The bay stays grounded until a new pilot is rerolled in.
      ctx.onFighterDown(s.pos.x, s.pos.y);
      ctx.onPilotKilled(s.pilotId);
      return false;
    }

    s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
    s.hitTimer += dt;
    for (const t of s.turrets) {
      t.flash = Math.max(0, t.flash - dt * 5);
      t.cooldown -= dt;
    }
    this.updateAbilities(s, dt, ctx); // Mk VI SWL: fighter missiles / drone beams

    // Just launched → fly straight out of the hangar for a beat before engaging.
    if (s.launchTimer > 0) {
      s.launchTimer -= dt;
      this.steer(s, dt, s.heading, s.spd);
      for (const t of s.turrets) t.angle = s.heading;
      return true;
    }

    // Combat over → roam around the ship for a few seconds, THEN fly home and dock.
    if (!ctx.active) {
      const dx = ctx.originX - s.pos.x;
      const dy = ctx.originY - s.pos.y;
      const dist = Math.hypot(dx, dy) || 1e-3;
      if (this.idleTime < POST_COMBAT_ROAM) {
        const tangent = Math.atan2(dy, dx) + Math.PI / 2;
        const pull = dist > PLAYER_FIGHTER.rallyRadius ? 1 : dist < 120 ? -1 : 0;
        let desired = Math.atan2(
          Math.sin(tangent) + (dy / dist) * pull * 0.8,
          Math.cos(tangent) + (dx / dist) * pull * 0.8,
        );
        desired = this.avoidRocks(s, ctx.bodies, desired);
        this.steer(s, dt, desired, s.spd * 0.7);
        for (const t of s.turrets) t.angle = s.heading;
        return true;
      }
      // Roam is over → head home; despawn on arrival at the hull.
      if (dx * dx + dy * dy < 70 * 70) return false;
      let desired = Math.atan2(dy, dx);
      desired = this.avoidRocks(s, ctx.bodies, desired);
      this.steer(s, dt, desired, s.spd);
      for (const t of s.turrets) t.angle = s.heading;
      return true;
    }

    // Leash: fighters stay near the mothership — a tight radius while recalled to
    // guard it, a looser one otherwise. Strayed past it → break off and fly home.
    const leash = guard ? PLAYER_FIGHTER.guardRange : PLAYER_FIGHTER.leashRange;
    const homeDx = ctx.originX - s.pos.x;
    const homeDy = ctx.originY - s.pos.y;
    const homeDist = Math.hypot(homeDx, homeDy) || 1e-3;
    if (homeDist > leash) {
      let desired = this.avoidRocks(s, ctx.bodies, Math.atan2(homeDy, homeDx));
      this.steer(s, dt, desired, s.spd);
      for (const t of s.turrets) t.angle = s.heading;
      if (s.hitTimer >= PLAYER_FIGHTER.regenDelay && s.shield < s.maxShield) {
        s.shield = Math.min(s.maxShield, s.shield + PLAYER_FIGHTER.shieldRegenPerSec * dt);
      }
      return true;
    }

    const target = this.pickTarget(s, ctx, leash);
    if (target) {
      const dx = target.pos.x - s.pos.x;
      const dy = target.pos.y - s.pos.y;
      const dist = Math.hypot(dx, dy) || 1e-3;
      const toT = Math.atan2(dy, dx);
      let desired: number;
      let speed: number;
      let firing = false;
      if (s.mode === "run") {
        desired = toT;
        speed = s.spd;
        firing = Math.abs(norm(toT - s.heading)) < PLAYER_FIGHTER.fireCone;
        if (dist < PLAYER_FIGHTER.passRange) {
          s.mode = "straight";
          s.straightTimer = PLAYER_FIGHTER.straightTime;
        }
      } else {
        desired = s.heading; // blow past, then curve back
        speed = s.spd * (s.straightTimer > PLAYER_FIGHTER.straightTime - 1 ? 1.3 : 1);
        s.straightTimer -= dt;
        if (s.straightTimer <= 0) s.mode = "run";
      }
      desired = this.avoidRocks(s, ctx.bodies, desired);
      this.steer(s, dt, desired, speed);
      for (const t of s.turrets) t.angle = s.heading;
      if (firing && dist < PLAYER_FIGHTER.turretRange) this.fireForward(s);
    } else {
      // In combat but nothing in reach → orbit the hull, waiting.
      const dx = ctx.originX - s.pos.x;
      const dy = ctx.originY - s.pos.y;
      const dist = Math.hypot(dx, dy) || 1e-3;
      const tangent = Math.atan2(dy, dx) + Math.PI / 2;
      const pull = dist > PLAYER_FIGHTER.rallyRadius ? 1 : dist < 120 ? -1 : 0;
      let desired = Math.atan2(
        Math.sin(tangent) + (dy / dist) * pull * 0.8,
        Math.cos(tangent) + (dx / dist) * pull * 0.8,
      );
      desired = this.avoidRocks(s, ctx.bodies, desired);
      this.steer(s, dt, desired, s.spd * 0.6);
      for (const t of s.turrets) t.angle = s.heading;
    }

    if (s.hitTimer >= PLAYER_FIGHTER.regenDelay && s.shield < s.maxShield) {
      s.shield = Math.min(s.maxShield, s.shield + PLAYER_FIGHTER.shieldRegenPerSec * dt);
    }
    return true;
  }

  /** Pick who to engage: first the nearest enemy fighter that is aggroed onto THIS
   * fighter (turn and fight whoever is hunting us), else the nearest hostile. */
  private pickTarget(s: PlayerFighter, ctx: PlayerFighterCtx, leash: number): Damageable | null {
    const leashSq = leash * leash; // only engage what's near the mothership
    let best: EnemyShip | null = null;
    // Commit to an attacker a bit past normal seek range (1.5×) so we turn back on it.
    let bestD = PLAYER_FIGHTER.seekRange * PLAYER_FIGHTER.seekRange * 2.25;
    for (const r of ctx.raiders) {
      if (r.hp <= 0 || !r.aggro || r.targetAlly !== s) continue;
      if ((r.pos.x - ctx.originX) ** 2 + (r.pos.y - ctx.originY) ** 2 > leashSq) continue;
      const d = (r.pos.x - s.pos.x) ** 2 + (r.pos.y - s.pos.y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = r;
      }
    }
    return best ?? this.nearestHostile(s, ctx, leash);
  }

  private nearestHostile(
    s: PlayerFighter,
    ctx: PlayerFighterCtx,
    leash: number,
  ): Damageable | null {
    const leashSq = leash * leash;
    let best: Damageable | null = null;
    let bestD = PLAYER_FIGHTER.seekRange * PLAYER_FIGHTER.seekRange;
    for (const h of ctx.hostiles) {
      if (h.hp <= 0) continue;
      if ((h.pos.x - ctx.originX) ** 2 + (h.pos.y - ctx.originY) ** 2 > leashSq) continue;
      const d = (h.pos.x - s.pos.x) ** 2 + (h.pos.y - s.pos.y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    return best;
  }

  private fireForward(s: PlayerFighter): void {
    const fx = Math.cos(s.heading);
    const fy = Math.sin(s.heading);
    const px = -fy;
    const py = fx;
    for (let j = 0; j < s.turrets.length; j++) {
      const t = s.turrets[j]!;
      if (t.cooldown > 0) continue;
      const off = PLAYER_FIGHTER.mounts[j]!;
      const mx = s.pos.x + fx * off.fwd + px * off.lat;
      const my = s.pos.y + fy * off.fwd + py * off.lat;
      for (let k = 0; k < PLAYER_FIGHTER.boltsPerShot; k++) {
        const bs = (k * 2 - (PLAYER_FIGHTER.boltsPerShot - 1)) * PLAYER_FIGHTER.barrelSpread;
        this.bullets.push({
          x: mx + px * bs + fx * PLAYER_FIGHTER.muzzleLen,
          y: my + py * bs + fy * PLAYER_FIGHTER.muzzleLen,
          vx: fx * PLAYER_FIGHTER.boltSpeed,
          vy: fy * PLAYER_FIGHTER.boltSpeed,
          life: PLAYER_FIGHTER.boltLife,
        });
      }
      t.cooldown = s.fireInterval;
      t.flash = 1;
    }
  }

  /** Mk VI Swarm-lord per-unit abilities (only in combat): escort fighters lob homing-missile
   * volleys at the nearest enemy; drones fire a short combat beam that also mines what it crosses. */
  private updateAbilities(s: PlayerFighter, dt: number, ctx: PlayerFighterCtx): void {
    if (s.launchTimer > 0) return;
    if (s.missileCd > 0) s.missileCd -= dt;
    if (!ctx.active) return; // abilities only fire while combat is active
    // Fighter homing missiles — a volley from the wings at the nearest hostile, on cooldown.
    if (
      ctx.canMissile &&
      s.missileCd <= 0 &&
      this.nearestHostile(s, ctx, PLAYER_FIGHTER.leashRange)
    ) {
      s.missileCd = FIGHTER_MISSILE.cooldown;
      const n = ctx.missileCount ?? FIGHTER_MISSILE.count; // fighters 2, drones 1
      const px = -Math.sin(s.heading);
      const py = Math.cos(s.heading);
      for (let k = 0; k < n; k++) {
        const lat = (k * 2 - (n - 1)) * 6; // fan out from the wings (n=1 → centred launch)
        this.missiles.push({
          pos: new Vector2(s.pos.x + px * lat, s.pos.y + py * lat),
          vel: new Vector2(),
          heading: s.heading,
          straight: FIGHTER_MISSILE.launchStraight,
          life: FIGHTER_MISSILE.life,
        });
      }
    }
  }

  private avoidRocks(s: PlayerFighter, bodies: MineableBody[], desired: number): number {
    let ax = 0;
    let ay = 0;
    for (const b of bodies) {
      if (b.ore <= 0) continue;
      const dx = s.pos.x - b.pos.x;
      const dy = s.pos.y - b.pos.y;
      const rr = b.radius + PLAYER_FIGHTER.collisionRadius + PLAYER_FIGHTER.avoidRange;
      const d2 = dx * dx + dy * dy;
      if (d2 < rr * rr && d2 > 1) {
        const d = Math.sqrt(d2);
        const w = (rr - d) / rr;
        ax += (dx / d) * w;
        ay += (dy / d) * w;
      }
    }
    if (ax === 0 && ay === 0) return desired;
    return Math.atan2(Math.sin(desired) + ay * 1.7, Math.cos(desired) + ax * 1.7);
  }

  private steer(s: PlayerFighter, dt: number, desired: number, speed: number): void {
    const diff = norm(desired - s.heading);
    const maxTurn = PLAYER_FIGHTER.turnRate * dt;
    s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(s.heading) * speed;
    const vy = Math.sin(s.heading) * speed;
    s.vel.set(vx, vy);
    s.pos.x += vx * dt;
    s.pos.y += vy * dt;
  }

  private damage(t: Damageable, amount: number): void {
    let dmg = amount;
    if (t.shield > 0) {
      const a = Math.min(t.shield, dmg);
      t.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) t.hp = Math.max(0, t.hp - dmg);
    t.shieldFlash = 1;
    t.hitTimer = 0;
  }
}
